import type { ReactNode } from "react"
import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { z } from "zod"

import {
	Button,
	Form,
	FormSwitch,
	Loader,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"

import { ResizablePanel } from "@/components/resizeable-panel"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"

export const AttachmentImportFormSchema = z.object({
	importDone: z.boolean(),
	intercom: z.boolean(),
	zendesk: z.boolean(),
	front: z.boolean(),
})

interface AttachmentImportFormProps {
	successContent: ReactNode
	errorContent: ReactNode
	showSkip?: boolean
}

export function AttachmentImportForm({
	successContent,
	errorContent,
	showSkip,
}: AttachmentImportFormProps) {
	const form = useZodForm({
		schema: AttachmentImportFormSchema,
		defaultValues: {
			importDone: false,
			intercom: true,
			zendesk: true,
			front: true,
		},
	})
	const importMutation = api.linear.attachmentImport.useMutation({
		onSuccess: (data) => {
			setSuccess(data.success)
			setCount(data.count)
			setUnsuccessfulIdentifiers(data.unsuccesfulIdentifiers)
			form.reset()
		},
		onError: () => {
			setSuccess(false)
			form.reset()
		},
	})
	const skipImportMutation = api.linear.skipImport.useMutation({
		onSuccess: () => {
			void navigate({ to: "/threads" })
		},
	})
	const [success, setSuccess] = useState<boolean>()
	const [count, setCount] = useState(0)
	const [unsuccessfulIdentifiers, setUnsuccessfulIdentifiers] = useState<
		Array<string>
	>([])
	const navigate = useNavigate()
	return (
		<Form
			form={form}
			onSubmit={async (values) => {
				analytics.track("attachment-import", { ...values })
				await importMutation.mutateAsync({
					front: values.front,
					includeDone: values.importDone,
					intercom: values.intercom,
					zendesk: values.zendesk,
				})
			}}
		>
			<div className="mb-4 text-base font-medium">Quick import</div>
			<div className="mb-6 text-secondary">
				If you use Linears integration for Intercom, Zendesk or Front, you can
				quickly import the issue attachments to get started with Productlane.
			</div>

			<ResizablePanel>
				{importMutation.isLoading ? (
					<div className="mt-4 flex flex-col items-center justify-center gap-4">
						<Loader />
						<div>Hang tight, this might take a moment.</div>
					</div>
				) : success === undefined ? (
					<>
						<FormSwitch name="intercom" label="Intercom" className="mb-2" />
						<FormSwitch name="zendesk" label="Zendesk" className="mb-2" />
						<FormSwitch name="front" label="Front" />
						<hr className="my-4 border-gray-200 dark:border-gray-700" />
						<FormSwitch
							name="importDone"
							label="Attachments from processed issues"
							className="mb-8"
						/>
					</>
				) : success ? (
					<div className="space-y-4">
						<div className="">
							Imported <strong>{count}</strong> notes. You can find them in the
							insight repository.
						</div>
						{unsuccessfulIdentifiers.length > 0 ? (
							<>
								<div>
									{unsuccessfulIdentifiers.length} attachments could not be
									imported. This can happen due to a variety of reasons as the
									Linear issue attachments are still in beta.
								</div>
								<div>
									The issue identifiers of the unsuccessful imports are:
								</div>
								<div>{unsuccessfulIdentifiers.join(", ")}</div>
							</>
						) : null}
					</div>
				) : (
					<div className="space-y-4">
						Something went wrong. We have been notified and will investigate why
						the import failed.
					</div>
				)}

				<div className="mx-auto mt-12 flex w-full flex-col items-end justify-end gap-1">
					{importMutation.isLoading ? null : success === undefined ? (
						<>
							<SubmitButton>Start import</SubmitButton>
							{showSkip && (
								<Button
									className="w-full"
									variant="ghost"
									onClick={() => {
										skipImportMutation.mutate()
									}}
								>
									Skip
								</Button>
							)}
						</>
					) : (
						<>
							{/* <Button
								variant="outline"
								onClick={() => {
									setSuccess(undefined)
								}}
								className=""
							>
								Restart
							</Button> */}
							{success ? successContent : errorContent}
						</>
					)}
				</div>
			</ResizablePanel>
		</Form>
	)
}
